import { useTransaction } from '../../../context/TransactionContext';
import { useUser } from '../../../context/UserContext';
import {
  ITransaction,
  IUpdateTransaction,
  ICreateTransaction,
} from '../../../interface/transaction';
import { InvoiceableType } from '../../../constants/matter';
import { CalculatePrice, TransactionUnitPriceSource } from '../../../constants/transaction';
import { getChangedFields } from '../../../constants/utilityFunctions';
import { SystemConstants, UserType } from '../../../constants/constants';
import { ITransactionParameters } from '../../../interface/parameters';

const toUpdateTransaction = (source: ITransaction | null): Partial<IUpdateTransaction> => ({
  ...source,
  skillId: source?.skill?.id ?? undefined,
  ignoreDiscount: source?.transactionType?.ignoreDiscount ?? false,
  transactionTypeId: source?.transactionType?.id ?? undefined,
  transactionUserId: source?.transactionUser?.id ?? undefined,
  matterPhaseId: source?.matterPhase?.id ?? null,
  priceSource: source?.priceSource as TransactionUnitPriceSource,
});

interface UseTransactionPayloadBuilderProps {
  transactionDate: string;
  billableFlag: boolean;
  parameters: ITransactionParameters;
  hidePricingInfo: boolean;
  userType: string;
}

export const useTransactionPayloadBuilder = ({
  transactionDate,
  billableFlag,
  parameters,
  hidePricingInfo,
  userType,
}: UseTransactionPayloadBuilderProps) => {
  const { transaction, initialTransaction } = useTransaction();
  const { user } = useUser();

  const getCalculatePricesValue = () => {
    return (transaction?.transactionType &&
      transaction?.transactionType?.standardPrice !== 0 &&
      transaction?.transactionType?.standardPrice !== null) ||
      userType === UserType.RESTRICTED
      ? CalculatePrice.default
      : CalculatePrice.totalPrice;
  };

  const getTotalPrice = (calculatePricesValue: string) => {
    if (calculatePricesValue !== CalculatePrice.totalPrice) {
      const isBillable = transaction?.matter?.invoiceableType !== InvoiceableType.nonBillable;
      return Number(
        isBillable ? transaction?.transactionType?.standardPrice?.toFixed(2) : (0).toFixed(2),
      );
    }
    return undefined;
  };

  const getUnitId = () => {
    return !parameters?.transactionTypeInUse ||
      (!parameters?.transactionTypeRequired && !transaction?.transactionType?.id)
      ? SystemConstants.UNIT_HOUR
      : undefined;
  };

  const getUnitPriceInCurrency = () => {
    return transaction?.matter?.currency === parameters?.currency
      ? undefined
      : Number(transaction?.unitPrice ?? 0);
  };

  const getInternalMatterPricing = () => {
    if (transaction?.matter?.invoiceableType !== InvoiceableType.internal) return {};

    if (transaction?.matter?.currency !== parameters?.currency) {
      return {
        unitPriceInCurrency: Number(transaction?.unitPrice ?? 0),
        unitPrice: Number(transaction?.unitPrice ?? 0),
        totalPriceInCurrency: 0,
      };
    }
    return {};
  };

  const getNonInternalMatterPricing = () => {
    if (transaction?.matter?.invoiceableType === InvoiceableType.internal) return {};

    if (transaction?.matter?.currency === parameters?.currency && !hidePricingInfo) {
      return { unitPrice: Number(transaction?.unitPrice ?? 0) };
    }
    return {};
  };

  const buildCommonTransactionPayload = (calculatePricesValue: string) => {
    const payload: Partial<
      ICreateTransaction & IUpdateTransaction & { overrideWarnings?: boolean }
    > = {
      subject: transaction?.currentSubject ?? '',
      transactionDate: transactionDate,
      billableQuantity:
        billableFlag && transaction?.matter?.invoiceableType !== InvoiceableType.internal
          ? Number(transaction?.billableQuantity ?? 0)
          : 0,
      workedHours: Number(transaction?.workedHours ?? 0),
      transactionUserId: user?.id ?? '',
      matterId: transaction?.matter?.id ?? '',
      transactionTypeId: parameters?.transactionTypeInUse
        ? transaction?.transactionType?.id
        : undefined,

      matterPhaseId: transaction?.matterPhaseId ?? undefined,
      ignoreDiscount: transaction?.transactionType?.ignoreDiscount ?? false,
      priceSource: transaction?.isManualUnitPrice
        ? TransactionUnitPriceSource.MANUAL
        : ((transaction?.priceSource as TransactionUnitPriceSource) ??
          TransactionUnitPriceSource.NOT_SET),
    };

    // Add conditional properties
    const totalPrice = getTotalPrice(calculatePricesValue);
    if (totalPrice !== undefined) payload.totalPrice = totalPrice;

    if (transaction?.overrideWarnings) payload.overrideWarnings = true;

    const unitId = getUnitId();
    if (unitId) payload.unitId = unitId;

    const unitPriceInCurrency = getUnitPriceInCurrency();
    if (unitPriceInCurrency !== undefined) payload.unitPriceInCurrency = unitPriceInCurrency;

    if (transaction?.skill?.id) payload.skillId = transaction?.skill?.id;

    // Add matter-specific pricing
    Object.assign(payload, getInternalMatterPricing());
    Object.assign(payload, getNonInternalMatterPricing());

    return payload;
  };

  const buildCreateTransactionPayload = (calculatePricesValue: string): ICreateTransaction => {
    return {
      ...buildCommonTransactionPayload(calculatePricesValue),
    } as ICreateTransaction;
  };

  const buildCreateTransactionParams = (calculatePricesValue: string) => {
    return {
      ...(!billableFlag && transaction?.matter?.invoiceableType === InvoiceableType.billable
        ? { invoiceableType: InvoiceableType.nonBillable }
        : { invoiceableType: InvoiceableType.billable }),
      calculatePrices: calculatePricesValue,
    };
  };

  const buildUpdateTransactionPayload = (calculatePricesValue: string): IUpdateTransaction => {
    return {
      ...buildCommonTransactionPayload(calculatePricesValue),
      // null rather than undefined: a cleared phase has to reach the server, and undefined is dropped from the request
      matterPhaseId: transaction?.matterPhaseId ?? null,
      version: transaction?.version,
    } as IUpdateTransaction;
  };

  const buildUpdateTransactionParams = (calculatePricesValue: string) => {
    return {
      calculatePrices: calculatePricesValue,
      ...(transaction?.matter?.invoiceableType !== InvoiceableType.internal
        ? {
            invoiceableType: billableFlag ? InvoiceableType.billable : InvoiceableType.nonBillable,
          }
        : {}),
    };
  };

  const getChangedFieldsPayload = (payload: IUpdateTransaction) => {
    return getChangedFields(toUpdateTransaction(initialTransaction), payload);
  };

  return {
    getCalculatePricesValue,
    buildCreateTransactionPayload,
    buildCreateTransactionParams,
    buildUpdateTransactionPayload,
    buildUpdateTransactionParams,
    getChangedFieldsPayload,
  };
};
